import { getPersistedAdminOptionsWithDefaults } from "@/utils/admin-options-manager";
import { setNetworkLogCaptureEnabled } from "@/utils/network-log/network-log-store";
import { useNetworkLogState } from "@/utils/network-log/use-network-log";
import { useDANGEROUSisChalkEmployee } from "@/utils/hooks/use-is-chalk-employee";
import { useSession } from "next-auth/react";
import dynamic from "next/dynamic";
import { useEffect, type FC } from "react";

const NetworkLogDrawer = dynamic(
  () => import("@/components/admin/NetworkLogDrawer").then((module) => module.NetworkLogDrawer),
  { ssr: false },
);

/**
 * Singleton mounted in NetworkingWrapper. Hosts the network log's global bottom
 * drawer and, Chrome DevTools-style, records gRPC traffic only while that drawer is
 * open (Chalk employees only, and never when the "Disable network log" admin option
 * is on). The drawer's open state persists in localStorage, and the store enables
 * capture at module load when it was open — so a recording session spans a reload
 * and includes the page load's own requests. Captured entries stay in the ring
 * buffer after the drawer closes, so a recorded session can still be reopened and
 * exported later.
 */
export const NetworkLogBootstrap: FC = () => {
  // ignoreAdminMode: the drawer is opened from the admin menu, but recording should
  // not silently stop if the admin-mode toggle is flipped while watching traffic.
  const isChalkEmployee = useDANGEROUSisChalkEmployee({ ignoreAdminMode: true });
  // While the session is resolving, isChalkEmployee is false even for employees;
  // acting on it then would clobber the capture the store restored for a reload.
  const sessionResolving = useSession().status === "loading";
  // Read once per mount; the admin-menu toggle reloads the page on change.
  const networkLogDisabled = getPersistedAdminOptionsWithDefaults().disableNetworkLog;
  const { panelOpen } = useNetworkLogState();

  useEffect(() => {
    // Hard-disable applies immediately; the employee-derived state waits for the
    // session so restored page-load capture isn't turned off then on again (the
    // gap would drop the very requests the restore exists to record).
    if (networkLogDisabled || !sessionResolving) {
      setNetworkLogCaptureEnabled(isChalkEmployee && !networkLogDisabled && panelOpen);
    }
    // Cleanup keeps capture from running invisibly on pages that install the
    // interceptor but not this bootstrap (the UnauthenticatedNetworkingWrapper tree)
    // after an SPA navigation away from the dashboard.
    return () => setNetworkLogCaptureEnabled(false);
  }, [isChalkEmployee, networkLogDisabled, panelOpen, sessionResolving]);

  if (!isChalkEmployee || networkLogDisabled) {
    return null;
  }

  return <NetworkLogDrawer />;
};
